import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));

    // Mode "sélection manuelle requise" : on désélectionne tout au chargement
    // et on garde l'add-to-cart bloqué tant qu'aucune variante n'a été choisie.
    if (this.hasAttribute('data-require-manual-selection')) {
      this.#initManualSelection();
    }
  }

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Sélection manuelle : marque que l'utilisateur a choisi quelque chose
    if (this.hasAttribute('data-require-manual-selection')) {
      const isPlaceholder =
        event.target instanceof HTMLSelectElement && event.target.value === '';

      if (isPlaceholder) {
        // L'utilisateur est revenu au placeholder : on remet à zéro
        this.removeAttribute('data-has-selection');
        const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
        if (hiddenInput) hiddenInput.value = '';
        return; // pas de fetch ni de dispatch pour un placeholder
      }

      this.setAttribute('data-has-selection', '');

      // Récupère l'ID de variante (sur l'option sélectionnée pour un select,
      // sur l'élément lui-même pour un radio)
      let variantId = event.target.dataset?.variantId;
      if (!variantId && event.target instanceof HTMLSelectElement) {
        const selectedOption = event.target.options[event.target.selectedIndex];
        variantId = selectedOption?.dataset.variantId;
      }
      if (variantId) {
        const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
        if (hiddenInput) hiddenInput.value = variantId;
      }

      // Supprime un éventuel message d'erreur affiché précédemment
      const existingWarning = this.querySelector('.variant-picker__warning');
      if (existingWarning) existingWarning.remove();
    }

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // En mode sélection manuelle, le HTML serveur revient SANS aucune option
    // pré-sélectionnée (c'est voulu côté Liquid). On capture donc l'état avant
    // la requête pour pouvoir le restaurer après le remplacement de innerHTML.
    const savedManualState = this.hasAttribute('data-require-manual-selection')
      ? this.#captureManualState()
      : null;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        this.updateVariantPicker(html);

        // Ré-applique le choix de l'utilisateur dans le HTML fraîchement remplacé
        if (savedManualState) this.#restoreManualState(savedManualState);

        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }
    this.innerHTML = newVariantPickerSource.innerHTML;
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const url = new URL(window.location.href);
    let variantId;

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }
    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error("Aucun ID de valeur d'option trouvé");
      return optionValueId;
    });
  }

  // ---------- Sélection manuelle requise ----------

  #initManualSelection() {
    this.#clearInitialSelection();

    const form = this.closest('form');
    if (form && !form.dataset.variantPickerGuardAttached) {
      form.dataset.variantPickerGuardAttached = 'true';
      form.addEventListener('submit', this.#guardFormSubmit.bind(this), { capture: true });
    }
  }

  // Supprime toute pré-sélection rendue par Shopify côté serveur.
  #clearInitialSelection() {
    const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
    if (hiddenInput) hiddenInput.value = '';

    this.querySelectorAll('fieldset input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    // Pour les <select>, on pointe explicitement sur l'option placeholder vide
    // car retirer "selected" ne suffit pas — le navigateur affiche sinon la
    // première option par défaut.
    this.querySelectorAll('select').forEach((select) => {
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
      const placeholderIndex = Array.from(select.options).findIndex((option) => option.value === '');
      if (placeholderIndex !== -1) {
        select.selectedIndex = placeholderIndex;
      }
    });

    this.removeAttribute('data-has-selection');
  }

  // Capture l'état actuel des sélections (utilisé avant un fetch).
  #captureManualState() {
    const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
    const selects = Array.from(this.querySelectorAll('select')).map((select) => ({
      name: select.name,
      value: select.value,
    }));
    const radios = Array.from(this.querySelectorAll('fieldset input[type="radio"]:checked')).map(
      (input) => ({ name: input.name, value: input.value }),
    );
    return {
      hiddenId: hiddenInput?.value || '',
      selects,
      radios,
    };
  }

  // Réapplique l'état capturé après un remplacement de innerHTML.
  #restoreManualState(state) {
    const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
    if (hiddenInput) hiddenInput.value = state.hiddenId;

    state.selects.forEach(({ name, value }) => {
      if (!value) return; // placeholder, on laisse tel quel
      const select = this.querySelector(`select[name="${CSS.escape(name)}"]`);
      if (!select) return;
      const option = Array.from(select.options).find((o) => o.value === value);
      if (option) {
        Array.from(select.options).forEach((o) => o.removeAttribute('selected'));
        option.setAttribute('selected', 'selected');
        select.value = value;
      }
    });

    state.radios.forEach(({ name, value }) => {
      const input = this.querySelector(
        `fieldset input[type="radio"][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`,
      );
      if (input) input.checked = true;
    });

    if (state.hiddenId) {
      this.setAttribute('data-has-selection', '');
    }
  }

  #guardFormSubmit(event) {
    const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
    if (hiddenInput && hiddenInput.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const message = this.dataset.noSelectionMessage || 'Veuillez sélectionner un modèle';
    this.#showWarning(message);
  }

  #showWarning(message) {
    const existing = this.querySelector('.variant-picker__warning');
    if (existing) existing.remove();

    const warning = document.createElement('div');
    warning.className = 'variant-picker__warning';
    warning.setAttribute('role', 'alert');
    warning.textContent = message;
    this.appendChild(warning);

    warning.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      if (warning.parentNode) warning.remove();
    }, 4000);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}